'use client';

import { Button, HStack, Spinner } from '@chakra-ui/react';
import { useToast } from '~/lib/toasts';
import { Link } from '@chakra-ui/next-js';
import { NestedHome } from '@habity/types';
import { useInterest } from '~/hooks/useInterest';
import { useUser } from '~/hooks/useUser';
import { useWishlist } from '~/hooks/useWishlist';
import {
  hasFollowedBiddingOrHigherInterest,
  hasSubscribedFinalPrice,
  isAddedToInterest,
  isAddedToWishlist,
  isSold,
  hasBiddingActive,
  hasUserBookedViewing,
  shouldShowPrice,
  shouldShowAddress,
} from '@habity/services';
import { ICONS } from '~/constants';
import { useModalWithUrl } from '~/hooks/useModalWithUrl';
import { useFilters } from '~/hooks/useFilters';
import { getRelatedHomesFilters } from '@habity/agencies';
import { getStreet } from '@habity/services';
import { useAgency } from '~/hooks/useAgency';
import { useState, useEffect, useRef } from 'react';
import { useAppStore } from '~/store';
import { revalidateHomeViewing } from '~/app/actions/revalidateViewing';
import { useUserAction } from '~/hooks/useUserAction';

interface DynamicCTAsProps {
  home: NestedHome;
}

/**
 * DynamicCTAs component that modifies action buttons based on home state:
 * - If is_bidding_active: Primary CTA is "Följ budgivning" and adds interest with level "followed_bidding"
 * - If has bookable viewings: Primary CTA is "Boka visning" and opens viewings modal
 * - Otherwise: Falls back to standard interest button
 * - Secondary CTA is always "Ställ en fråga" if a broker phone is available
 */
export function DynamicCTAs({ home }: DynamicCTAsProps) {
  return (
    <HStack
      gap={{ mobile: '4', desktop: '2' }}
      w="100%"
      maxW={{ mobile: '500px', desktop: '400px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <PrimaryCTA home={home} />
      <SecondaryCTA home={home} />
    </HStack>
  );
}

interface CTAProps {
  home: NestedHome;
}

function PrimaryCTA({ home }: CTAProps) {
  const { removeFromInterest, addToInterest, isAddingInterest } = useInterest();
  const { user } = useUser();
  const { handleOpenWithUrlUpdate } = useModalWithUrl(home);
  const { createSearchPathFromFilters } = useFilters();
  const { identifier } = useAgency();
  const { showToast } = useToast();

  // Local state for optimistic updates
  const [isFollowingBidding, setIsFollowingBidding] = useState<boolean>(
    user ? hasFollowedBiddingOrHigherInterest(user, home.id) : false
  );
  const [hasShownInterest, setHasShownInterest] = useState<boolean>(
    user ? isAddedToInterest(user, home.id, 'showed_interest') : false
  );

  // Track loading state for remove actions only (add actions use isAddingInterest from hook)
  const [isRemovingBidding, setIsRemovingBidding] = useState<boolean>(false);
  const [isRemovingInterest, setIsRemovingInterest] = useState<boolean>(false);

  // Track optimistic updates to prevent useEffect from resetting state
  const optimisticBiddingRef = useRef<boolean | null>(null);
  const optimisticInterestRef = useRef<boolean | null>(null);

  const isMobile = useAppStore((state) => state.isMobile);

  // Sync local state with server state when user data changes
  useEffect(() => {
    if (user) {
      const serverBidding = hasFollowedBiddingOrHigherInterest(user, home.id);
      const serverInterest = isAddedToInterest(
        user,
        home.id,
        'showed_interest'
      );

      // Only update if no optimistic update pending or server matches optimistic
      if (
        optimisticBiddingRef.current === null ||
        optimisticBiddingRef.current === serverBidding
      ) {
        setIsFollowingBidding(serverBidding);
        optimisticBiddingRef.current = null;
      }
      if (
        optimisticInterestRef.current === null ||
        optimisticInterestRef.current === serverInterest
      ) {
        setHasShownInterest(serverInterest);
        optimisticInterestRef.current = null;
      }
    } else {
      setIsFollowingBidding(false);
      setHasShownInterest(false);
      optimisticBiddingRef.current = null;
      optimisticInterestRef.current = null;
    }
  }, [user?.id, user?.interests, home.id]);

  // Check if home has active bidding and bidding is visible on site
  const hasBiddingActiveState = hasBiddingActive(home);

  // Check if home has bookable viewings
  const hasBookableViewings =
    home.viewings?.some(
      (viewing) =>
        // viewing.is_bookable &&
        viewing.is_visible && new Date(viewing.start_time) > new Date()
    ) || false;

  // Check if user has booked any viewing
  const hasBookedViewing = hasUserBookedViewing(user, home);

  // Handle following bidding
  const handleFollowBidding = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAddingInterest || isRemovingBidding) return;

    if (isFollowingBidding) {
      // Remove - optimistic update
      setIsFollowingBidding(false);
      setIsRemovingBidding(true);
      try {
        await removeFromInterest(home, 'followed_bidding');
      } catch (error) {
        console.error('Error removing bidding follow:', error);
        setIsFollowingBidding(true); // Revert on error
        if (!isMobile)
          showToast(
            {
              title: 'Något gick fel',
              description: 'Försök igen senare',
            },
            { status: 'error' }
          );
      } finally {
        setIsRemovingBidding(false);
      }
    } else {
      // Add - optimistic update for logged-in users
      if (user) {
        setIsFollowingBidding(true);
        optimisticBiddingRef.current = true;
      }
      await addToInterest(home, 'followed_bidding');
    }
  };

  // Handle booking viewing
  const handleBookViewing = (e: React.MouseEvent) => {
    e.stopPropagation();

    handleOpenWithUrlUpdate({
      modalType: 'viewings',
      open: true,
    });
  };

  // Get search path for related homes
  const getRelatedHomesSearchPath = () => {
    // Get related homes filters
    const relatedFilters = identifier
      ? getRelatedHomesFilters(home, identifier)
      : null;

    // Make sure we're explicitly setting the tab to 'homes'
    return createSearchPathFromFilters({
      filters: relatedFilters,
      includeExistingFilters: false,
      tab: 'homes',
    });
  };

  // Handle standard interest button (PrimaryCTA version)
  const handleInterestToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAddingInterest || isRemovingInterest) return;

    if (hasShownInterest) {
      // Remove - optimistic update
      setHasShownInterest(false);
      setIsRemovingInterest(true);
      try {
        await removeFromInterest(home, 'showed_interest');
        await revalidateHomeViewing(home);
      } catch (error) {
        console.error('Error removing interest:', error);
        setHasShownInterest(true); // Revert on error
        if (!isMobile)
          showToast(
            {
              title: 'Något gick fel',
              description: 'Försök igen senare',
            },
            { status: 'error' }
          );
      } finally {
        setIsRemovingInterest(false);
      }
    } else {
      // Add - optimistic update for logged-in users
      if (user) {
        setHasShownInterest(true);
        optimisticInterestRef.current = true;
      }
      await addToInterest(home, 'showed_interest');
    }
  };

  // Combined loading states
  const isLoadingBidding = isAddingInterest || isRemovingBidding;
  const isLoadingInterest = isAddingInterest || isRemovingInterest;

  if (isSold(home)) {
    return (
      <Button
        as={Link}
        href={getRelatedHomesSearchPath()}
        variant="solid"
        leftIcon={<ICONS.SEARCH />}
        flex="1"
      >
        Hitta liknande
      </Button>
    );
  }

  if (hasBiddingActiveState) {
    return (
      <Button
        variant="solid"
        onClick={handleFollowBidding}
        leftIcon={
          isLoadingBidding ? (
            <Spinner size="sm" />
          ) : isFollowingBidding ? (
            <ICONS.BID_ACTIVE />
          ) : (
            <ICONS.BID />
          )
        }
        flex="1"
      >
        {isLoadingBidding
          ? 'Uppdaterar...'
          : isFollowingBidding
            ? 'Följer budgivning'
            : 'Följ budgivning'}
      </Button>
    );
  }

  if (hasBookableViewings) {
    return (
      <Button
        variant="solid"
        onClick={handleBookViewing}
        leftIcon={<ICONS.CALENDAR />}
        flex="1"
      >
        {hasBookedViewing ? 'Avboka visning' : 'Boka visning'}
      </Button>
    );
  }

  return (
    <Button
      variant="solid"
      onClick={handleInterestToggle}
      leftIcon={
        isLoadingInterest ? (
          <Spinner size="sm" />
        ) : hasShownInterest ? (
          <ICONS.INTEREST_ACTIVE />
        ) : (
          <ICONS.INTEREST />
        )
      }
      flex="1"
    >
      {isLoadingInterest
        ? 'Uppdaterar...'
        : hasShownInterest
          ? 'Intresserad'
          : 'Anmäl intresse'}
    </Button>
  );
}

function SecondaryCTA({ home }: CTAProps) {
  const { removeFromInterest, addToInterest, isAddingInterest } = useInterest();
  const { addToWishlist, removeFromWishlist, isAddingToWishlist } =
    useWishlist();
  const { user } = useUser();
  const { responsible_broker } = home || {};
  const { showToast } = useToast();
  const { executeAction, isLoading: isSubscribingFinalPrice } = useUserAction();

  // Local state for optimistic updates
  const [hasShownInterest, setHasShownInterest] = useState<boolean>(
    user ? isAddedToInterest(user, home.id, 'showed_interest') : false
  );
  const [isInWishlist, setIsInWishlist] = useState<boolean>(
    user ? isAddedToWishlist(user, home.id) : false
  );
  const [isSubscribedFinalPrice, setIsSubscribedFinalPrice] = useState<boolean>(
    user ? hasSubscribedFinalPrice(user, home.id) : false
  );

  // Track loading state for remove actions only (add actions use hooks)
  const [isRemovingInterest, setIsRemovingInterest] = useState<boolean>(false);
  const [isRemovingFromWishlist, setIsRemovingFromWishlist] =
    useState<boolean>(false);

  // Track optimistic updates to prevent useEffect from resetting state
  const optimisticInterestRef = useRef<boolean | null>(null);
  const optimisticWishlistRef = useRef<boolean | null>(null);

  const isMobile = useAppStore((state) => state.isMobile);

  // Sync local state with server state when user data changes
  useEffect(() => {
    if (user) {
      const serverInterest = isAddedToInterest(
        user,
        home.id,
        'showed_interest'
      );
      const serverWishlist = isAddedToWishlist(user, home.id);

      const serverSubscribedFinalPrice = hasSubscribedFinalPrice(user, home.id);

      if (
        optimisticInterestRef.current === null ||
        optimisticInterestRef.current === serverInterest
      ) {
        setHasShownInterest(serverInterest);
        optimisticInterestRef.current = null;
      }
      if (
        optimisticWishlistRef.current === null ||
        optimisticWishlistRef.current === serverWishlist
      ) {
        setIsInWishlist(serverWishlist);
        optimisticWishlistRef.current = null;
      }
      setIsSubscribedFinalPrice(serverSubscribedFinalPrice);
    } else {
      setHasShownInterest(false);
      setIsInWishlist(false);
      setIsSubscribedFinalPrice(false);
      optimisticInterestRef.current = null;
      optimisticWishlistRef.current = null;
    }
  }, [user?.id, user?.interests, home.id]);

  // Check if home has active bidding and bidding is visible on site
  const hasBiddingActiveState = hasBiddingActive(home);

  // Check if home has bookable viewings
  const hasBookableViewings =
    home.viewings?.some(
      (viewing) =>
        // viewing.is_bookable &&
        viewing.is_visible && new Date(viewing.start_time) > new Date()
    ) || false;

  const hasProject = home.project_id !== null;

  // Handle standard interest button (SecondaryCTA version)
  const handleInterestToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAddingInterest || isRemovingInterest) return;

    if (hasShownInterest) {
      // Remove - optimistic update
      setHasShownInterest(false);
      setIsRemovingInterest(true);
      try {
        await removeFromInterest(home, 'showed_interest');
        await revalidateHomeViewing(home);
      } catch (error) {
        console.error('Error removing interest:', error);
        setHasShownInterest(true); // Revert on error
        if (!isMobile)
          showToast(
            {
              title: 'Något gick fel',
              description: 'Försök igen senare',
            },
            { status: 'error' }
          );
      } finally {
        setIsRemovingInterest(false);
      }
    } else {
      // Add - optimistic update for logged-in users
      if (user) {
        setHasShownInterest(true);
        optimisticInterestRef.current = true;
      }
      await addToInterest(home, 'showed_interest');
    }
  };

  // Combined loading state for interest
  const isLoadingInterest = isAddingInterest || isRemovingInterest;

  // Handle toggling wishlist status
  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAddingToWishlist || isRemovingFromWishlist) return;

    if (isInWishlist) {
      // Remove from wishlist - optimistic update
      setIsInWishlist(false);
      setIsRemovingFromWishlist(true);
      try {
        await removeFromWishlist(home);
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        setIsInWishlist(true); // Revert on error
        if (!isMobile)
          showToast(
            {
              title: 'Något gick fel',
              description: 'Försök igen senare',
            },
            { status: 'error' }
          );
      } finally {
        setIsRemovingFromWishlist(false);
      }
    } else {
      // Add to wishlist - optimistic update for logged-in users
      if (user) {
        setIsInWishlist(true);
        optimisticWishlistRef.current = true;
      }
      await addToWishlist(home);
    }
  };

  if (isSold(home) && responsible_broker && responsible_broker.phone) {
    return (
      <Button
        as={Link}
        href={`sms:${responsible_broker.phone
          }&body=Ang liknande objekt som ${getStreet(home)}\n\n`}
        variant={{ mobile: 'outline', desktop: 'secondary' }}
        leftIcon={<ICONS.SMS />}
        flex="1"
      >
        Kontakta mäklare
      </Button>
    );
  }

  if (hasBookableViewings) {
    return (
      <Button
        variant={{ mobile: 'outline', desktop: 'secondary' }}
        onClick={handleInterestToggle}
        leftIcon={
          isLoadingInterest ? (
            <Spinner size="sm" />
          ) : hasShownInterest ? (
            <ICONS.INTEREST_ACTIVE />
          ) : (
            <ICONS.INTEREST />
          )
        }
        flex="1"
      >
        {isLoadingInterest
          ? 'Uppdaterar...'
          : hasShownInterest
            ? 'Intresserad'
            : 'Anmäl intresse'}
      </Button>
    );
  }

  // When bidding is active (primary CTA is "Följ budgivning"), show "Bevaka slutpris" if conditions met
  if (hasBiddingActiveState) {
    if (shouldShowPrice(home) && shouldShowAddress(home)) {
      const handleSubscribeFinalPrice = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isSubscribedFinalPrice) return;
        const result = await executeAction({
          home,
          action: 'subscribed_final_price',
        });
        if (result.success && !result.needsAuth) {
          setIsSubscribedFinalPrice(true);
        }
      };

      return (
        <Button
          variant={{ mobile: 'outline', desktop: 'secondary' }}
          onClick={handleSubscribeFinalPrice}
          leftIcon={
            isSubscribingFinalPrice ? (
              <Spinner size="sm" />
            ) : (
              <ICONS.HOMEBUYERS_ALERT />
            )
          }
          flex="1"
          isDisabled={isSubscribingFinalPrice || isSubscribedFinalPrice}
        >
          {isSubscribedFinalPrice
            ? 'Slutpris bevakas'
            : isSubscribingFinalPrice
              ? 'Bevakar...'
              : 'Bevaka slutpris'}
        </Button>
      );
    }
  }

  if (hasProject) {
    return (
      <Button
        as={Link}
        href={`/projects/${home.project_id}`}
        variant="outline"
        leftIcon={<ICONS.PROJECT />}
        flex="1"
      >
        Till projektet
      </Button>
    );
  }

  // Show "Bevaka slutpris" when price is visible (home is for sale with visible price)
  // and when address is visible (status.show_address is true)
  if (
    shouldShowPrice(home) &&
    shouldShowAddress(home) &&
    !hasBookableViewings &&
    !hasBiddingActiveState
  ) {
    const handleSubscribeFinalPrice = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isSubscribedFinalPrice) return;
      const result = await executeAction({
        home,
        action: 'subscribed_final_price',
      });
      if (result.success && !result.needsAuth) {
        setIsSubscribedFinalPrice(true);
      }
    };

    return (
      <Button
        variant={{ mobile: 'outline', desktop: 'secondary' }}
        onClick={handleSubscribeFinalPrice}
        leftIcon={
          isSubscribingFinalPrice ? (
            <Spinner size="sm" />
          ) : (
            <ICONS.HOMEBUYERS_ALERT />
          )
        }
        flex="1"
        isDisabled={isSubscribingFinalPrice || isSubscribedFinalPrice}
      >
        {isSubscribedFinalPrice
          ? 'Slutpris bevakas'
          : isSubscribingFinalPrice
            ? 'Bevakar...'
            : 'Bevaka slutpris'}
      </Button>
    );
  }

  if (responsible_broker && !hasBookableViewings && !hasBiddingActiveState) {
    return (
      <Button
        as={Link}
        href={
          isMobile
            ? `sms:${responsible_broker.phone || ''}&body=Ang ${getStreet(
              home
            )}%0D%0A%0D%0A`
            : `mailto:${responsible_broker.email || ''}?subject=Ang ${getStreet(
              home
            )}&body=Hej,%0D%0A%0D%0AJag är intresserad av ${getStreet(
              home
            )} och har en fråga.%0D%0A%0D%0AMed vänliga hälsningar`
        }
        variant={{ mobile: 'outline', desktop: 'secondary' }}
        leftIcon={isMobile ? <ICONS.SMS /> : <ICONS.MAIL />}
        flex="1"
      >
        Ställ en fråga
      </Button>
    );
  }

  const isWishlistLoading = isAddingToWishlist || isRemovingFromWishlist;

  return (
    <Button
      variant={{ mobile: 'outline', desktop: 'secondary' }}
      onClick={handleWishlistToggle}
      leftIcon={
        isWishlistLoading ? (
          <Spinner size="sm" />
        ) : isInWishlist ? (
          <ICONS.WISHLIST_ACTIVE />
        ) : (
          <ICONS.WISHLIST />
        )
      }
      flex="1"
    >
      {isWishlistLoading ? 'Uppdaterar...' : isInWishlist ? 'Sparat' : 'Spara'}
    </Button>
  );
}
